import React from 'react';
import { Link } from 'react-router-dom';
import { Search, ShieldCheck, Globe, FileCheck, ArrowRight, Users, LineChart, BookOpen } from 'lucide-react';

export const HomePage: React.FC = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=2850&q=80"
            alt="Lab background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900/50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
             <div>
                <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-6 leading-tight">
                  The Global Marketplace for <span className="text-blue-400">Regulatory Expertise</span>
                </h1>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  RegulatorySherpas connects biotech companies with verified regulatory consultancies. 
                  Find specialized experts for FDA, EMA, and PMDA submissions, orphan designations, and strategy.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to="/directory"
                    className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg text-slate-900 bg-blue-400 hover:bg-blue-500 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Find a Consultancy
                  </Link>
                  <Link 
                    to="/pricing"
                    className="inline-flex items-center justify-center px-8 py-4 border border-slate-500 text-base font-medium rounded-lg text-white bg-slate-800 hover:bg-slate-700 transition-all backdrop-blur-sm bg-opacity-60"
                  >
                    List Your Consultancy
                  </Link>
                </div>
             </div>

             <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-slate-700 shadow-2xl">
                   <h3 className="text-white font-semibold mb-6 flex items-center gap-2"><LineChart className="w-5 h-5 text-blue-400"/> Trending Searches</h3>
                   <div className="space-y-4">
                      <div className="flex items-center justify-between text-slate-300 hover:bg-white/5 p-3 rounded-lg cursor-pointer transition-colors">
                         <span>FDA Orphan Drug Designation</span>
                         <ArrowRight className="w-4 h-4"/>
                      </div>
                      <div className="flex items-center justify-between text-slate-300 hover:bg-white/5 p-3 rounded-lg cursor-pointer transition-colors">
                         <span>EMA Cell & Gene Therapy</span>
                         <ArrowRight className="w-4 h-4"/>
                      </div>
                      <div className="flex items-center justify-between text-slate-300 hover:bg-white/5 p-3 rounded-lg cursor-pointer transition-colors">
                         <span>Biosimilar 351(k) Pathway</span>
                         <ArrowRight className="w-4 h-4"/>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* Two-Sided Value Prop */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              {/* For Pharma */}
              <div>
                 <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold uppercase tracking-wide mb-4">
                    For Pharma & Biotech
                 </div>
                 <h2 className="text-3xl font-bold text-slate-900 mb-6">De-risk your regulatory strategy.</h2>
                 <ul className="space-y-6">
                    <li className="flex items-start">
                       <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                          <ShieldCheck className="w-6 h-6 text-blue-600" />
                       </div>
                       <div className="ml-4">
                          <h3 className="text-lg font-medium text-slate-900">Verified Track Records</h3>
                          <p className="text-slate-600 mt-1">Stop guessing. See which consultancies have actually secured Orphan Designations or BLA approvals in your therapeutic area.</p>
                       </div>
                    </li>
                    <li className="flex items-start">
                       <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Globe className="w-6 h-6 text-blue-600" />
                       </div>
                       <div className="ml-4">
                          <h3 className="text-lg font-medium text-slate-900">Global Expertise</h3>
                          <p className="text-slate-600 mt-1">Filter experts by Health Authority (FDA, EMA, NMPA, ANVISA) to find local guides for global markets.</p>
                       </div>
                    </li>
                 </ul>
                 <div className="mt-8">
                    <Link to="/directory" className="text-blue-600 font-medium hover:text-blue-800 flex items-center gap-1">
                       Browse Directory <ArrowRight className="w-4 h-4"/>
                    </Link>
                 </div>
              </div>

              {/* For Consultancies */}
              <div>
                 <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold uppercase tracking-wide mb-4">
                    For Consultancies
                 </div>
                 <h2 className="text-3xl font-bold text-slate-900 mb-6">Showcase your expertise to the right buyers.</h2>
                 <ul className="space-y-6">
                    <li className="flex items-start">
                       <div className="flex-shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-600" />
                       </div>
                       <div className="ml-4">
                          <h3 className="text-lg font-medium text-slate-900">High-Intent Leads</h3>
                          <p className="text-slate-600 mt-1">Get discovered by regulatory teams actively searching for your specific niche expertise.</p>
                       </div>
                    </li>
                    <li className="flex items-start">
                       <div className="flex-shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                          <FileCheck className="w-6 h-6 text-purple-600" />
                       </div>
                       <div className="ml-4">
                          <h3 className="text-lg font-medium text-slate-900">Differentiate with Data</h3>
                          <p className="text-slate-600 mt-1">Stand out from generalists by verifying your specific approval history and therapeutic depth.</p>
                       </div>
                    </li>
                 </ul>
                 <div className="mt-8">
                    <Link to="/pricing" className="text-purple-600 font-medium hover:text-purple-800 flex items-center gap-1">
                       View Membership Plans <ArrowRight className="w-4 h-4"/>
                    </Link>
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-slate-50 border-y border-slate-200 py-16">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
               <div>
                  <div className="text-4xl font-bold text-slate-900">500+</div>
                  <div className="text-sm text-slate-500 mt-1 uppercase tracking-wide">Consultancies</div>
               </div>
               <div>
                  <div className="text-4xl font-bold text-slate-900">50+</div>
                  <div className="text-sm text-slate-500 mt-1 uppercase tracking-wide">Health Authorities</div>
               </div>
               <div>
                  <div className="text-4xl font-bold text-slate-900">1,200+</div>
                  <div className="text-sm text-slate-500 mt-1 uppercase tracking-wide">Verified Approvals</div>
               </div>
               <div>
                  <div className="text-4xl font-bold text-slate-900">24h</div>
                  <div className="text-sm text-slate-500 mt-1 uppercase tracking-wide">Avg. Response Time</div>
               </div>
            </div>
         </div>
      </div>
      
      {/* CTA */}
      <div className="bg-blue-900 py-16">
         <div className="max-w-4xl mx-auto px-4 text-center">
             <h2 className="text-3xl font-bold text-white mb-6">Ready to accelerate your submission?</h2>
             <Link to="/directory" className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-lg text-blue-900 bg-white hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl">
               Find Your Regulatory Partner
             </Link>
         </div>
      </div>
    </div>
  );
};